import React from "react";
import { motion } from "framer-motion";
import {
  Truck,
  Container,
  Globe2,
  Snowflake,
  Layers,
  Construction,
  Zap,
  Boxes,
  HardHat,
  ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "@/constants/testIds";

const services = [
  {
    id: "ftl",
    icon: Truck,
    title: "Full Truckload (FTL)",
    desc: "Dedicated trucks for full-load shipments — fast, direct, and the most cost-effective option for high volumes.",
    tag: "Road",
  },
  {
    id: "ltl",
    icon: Layers,
    title: "Less-than-Truckload",
    desc: "Share capacity with other shippers and only pay for the space you use. Ideal for SMEs and smaller consignments.",
    tag: "Groupage",
  },
  {
    id: "cross-border",
    icon: Globe2,
    title: "Cross-Border SADC",
    desc: "Botswana, Namibia, Zambia, Zimbabwe, Mozambique and beyond. We handle the documentation, border clearance and coordination.",
    tag: "Regional",
  },
  {
    id: "abnormal",
    icon: Construction,
    title: "Abnormal & Heavy Haul",
    desc: "Oversized cargo, project equipment, mining and construction loads — permitted, escorted and delivered.",
    tag: "Specialised",
  },
  {
    id: "container",
    icon: Container,
    title: "Containerised Freight",
    desc: "Port to door, depot to port. Reliable container haulage between Durban, Cape Town, Port Elizabeth and inland terminals.",
    tag: "Container",
  },
  {
    id: "bulk",
    icon: Boxes,
    title: "Bulk Commodities",
    desc: "Tippers, tautliners and side-tippers for bulk minerals, grain, fertilizer and aggregates — at scale.",
    tag: "Bulk",
  },
  {
    id: "reefer",
    icon: Snowflake,
    title: "Refrigerated · Reefer",
    desc: "Temperature-controlled transport for perishables, pharmaceuticals and FMCG cold chain — handled end to end.",
    tag: "Cold Chain",
  },
  {
    id: "project",
    icon: HardHat,
    title: "Project Logistics",
    desc: "Multi-modal coordination for mining, energy and construction projects — schedules, permits and crews aligned.",
    tag: "Projects",
  },
  {
    id: "express",
    icon: Zap,
    title: "Express · Time-Critical",
    desc: "Urgent, deadline-driven loads. Direct dispatch with one of our vetted carriers — usually rolling within hours.",
    tag: "Urgent",
  },
];

export default function Services() {
  return (
    <section
      id="what"
      data-testid={SERVICES.section}
      className="relative py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-7">
            <div className="text-xs md:text-sm uppercase tracking-[0.24em] font-bold text-[#0070C0]">
              What we move
            </div>
            <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#002050] leading-[1.05]">
              The right truck for every load — every time.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-lg text-[#1F2937]/75 leading-relaxed">
              Whatever you ship, wherever it needs to go in Southern Africa,
              we'll match it to the right truck and the right operator from our
              vetted network. One call. One contact. One invoice.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.a
                key={s.id}
                href="#quote"
                data-testid={SERVICES.card(s.id)}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
                className="group relative bg-white rounded-2xl p-7 md:p-8 border border-[#E2E8F0] hover:border-[#0070C0]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(0,32,80,0.25)] flex flex-col h-full"
              >
                <div className="flex items-start justify-between">
                  <div className="h-12 w-12 rounded-xl bg-[#0070C0]/10 group-hover:bg-[#0070C0] flex items-center justify-center transition-colors duration-300">
                    <Icon
                      className="h-6 w-6 text-[#0070C0] group-hover:text-white transition-colors"
                      strokeWidth={1.75}
                    />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#0070C0] bg-[#00AEEF]/10 px-2.5 py-1 rounded-full">
                    {s.tag}
                  </span>
                </div>
                <h3 className="font-display mt-6 text-[22px] md:text-2xl font-bold text-[#002050] tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-3 text-[15px] text-[#1F2937]/75 leading-relaxed flex-1">
                  {s.desc}
                </p>
                <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0070C0]">
                  Request a rate
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
